/*
  Cart upsell (snippets/cart-upsell.liquid).
  The recommendations API takes one product, so it is called once per cart product and the
  results are interleaved: 1 cart product gives 6 of its own, 2 give 3 + 3, 3 give 2 + 2 + 2.
  A cart product without complementary products uses its related ones instead (same as the PDP
  Frequently Bought, data-related). Anything already in the cart is skipped, then the fallback
  collection fills the rest.
  The drawer re-renders on every cart change, so each render gets a fresh element; responses are
  cached per product for the page view so that stays instant.
*/
if (!customElements.get('cart-upsell')) {
  const cartUpsellCache = new Map();

  customElements.define('cart-upsell', class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.onOutsideClick = (e) => {
        if (!e.target.closest('.cu-vr')) this.closeVariants();
      };
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('.cu-vr-btn');
        if (toggle) return this.toggleVariants(toggle);
        const option = e.target.closest('.cu-vr-opt');
        if (option && !option.disabled) this.pickVariant(option);
      });
      // keyup, because the cart drawer closes itself on Escape keyup
      this.addEventListener('keyup', (e) => {
        if (e.code !== 'Escape' || !this.openToggle) return;
        e.stopPropagation();
        this.closeVariants(true);
      });
    }

    // The dropdown is positioned against this element, not the card, so the scrolling
    // card row does not clip it
    toggleVariants(toggle) {
      const wasOpen = this.openToggle === toggle;
      this.closeVariants();
      if (wasOpen) return;

      const list = toggle.nextElementSibling;
      const box = this.getBoundingClientRect();
      const rect = toggle.getBoundingClientRect();
      list.style.top = `${rect.bottom - box.top + 4}px`;
      list.style.left = `${rect.left - box.left}px`;
      list.hidden = false;
      const overflow = list.offsetLeft + list.offsetWidth - this.clientWidth;
      if (overflow > 0) list.style.left = `${Math.max(0, list.offsetLeft - overflow)}px`;

      toggle.setAttribute('aria-expanded', 'true');
      this.openToggle = toggle;
      document.addEventListener('click', this.onOutsideClick);
      const current = list.querySelector('.is-active:not([disabled])') || list.querySelector('.cu-vr-opt:not([disabled])');
      if (current) current.focus({ preventScroll: true });
    }

    closeVariants(returnFocus) {
      const toggle = this.openToggle;
      if (!toggle) return;
      toggle.nextElementSibling.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      this.openToggle = null;
      document.removeEventListener('click', this.onOutsideClick);
      if (returnFocus) toggle.focus();
    }

    pickVariant(option) {
      const card = option.closest('.cu-card');
      card.querySelector('[name="id"]').value = option.dataset.id;
      // money output can carry markup (<span class=money>), so set it as HTML
      card.querySelector('.cu-price').innerHTML = option.dataset.price;
      card.querySelector('.cu-vr-btn span').textContent = option.textContent;
      card.querySelectorAll('.cu-vr-opt').forEach((opt) => opt.classList.toggle('is-active', opt === option));
      this.closeVariants(true);
    }

    connectedCallback() {
      const drawer = this.closest('#CartDrawer');
      if (!drawer) {
        this.load();
        return;
      }
      // The closed drawer sits just off the right edge of the screen; load once it slides in
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.intersectionRatio > 0)) return;
        this.observer.disconnect();
        this.load();
      }, { threshold: 0.01 });
      this.observer.observe(drawer);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('click', this.onOutsideClick);
    }

    fetchCards(productId, intent) {
      const url = `${this.dataset.url}&intent=${intent}&product_id=${productId}`;
      if (!cartUpsellCache.has(url)) {
        cartUpsellCache.set(url, fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => ''));
      }
      return cartUpsellCache.get(url).then((html) =>
        [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.cu-card')]);
    }

    recommendationsFor(productId) {
      return this.fetchCards(productId, 'complementary').then((cards) =>
        (cards.length || !this.hasAttribute('data-related') ? cards : this.fetchCards(productId, 'related')));
    }

    load() {
      const sources = this.dataset.sources.split(',').filter(Boolean);
      const limit = parseInt(this.dataset.limit, 10) || 6;
      const seen = new Set(this.dataset.exclude.split(',').filter(Boolean));
      const picked = [];
      const take = (card) => {
        const id = card.dataset.productId;
        if (picked.length >= limit || seen.has(id)) return;
        seen.add(id);
        picked.push(card);
      };

      Promise.all(sources.map((id) => this.recommendationsFor(id))).then((lists) => {
        const longest = Math.max(0, ...lists.map((list) => list.length));
        for (let i = 0; i < longest; i++) lists.forEach((list) => list[i] && take(list[i]));

        const fallback = this.querySelector('template[data-cu-fallback]');
        if (fallback) fallback.content.querySelectorAll('.cu-card').forEach(take);

        if (!picked.length) return;
        const list = this.querySelector('[data-cu-list]');
        picked.forEach((card) => list.appendChild(document.importNode(card, true)));
        this.hidden = false;
        list.addEventListener('scroll', () => this.closeVariants(), { passive: true });
        if (picked.length > 1) this.initNav(list);
      });
    }

    // One card per slide; the arrows page by the list width and grey out at either end
    initNav(list) {
      const prev = this.querySelector('[data-cu-prev]');
      const next = this.querySelector('[data-cu-next]');
      const update = () => {
        prev.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
      };
      prev.addEventListener('click', () => list.scrollBy({ left: -list.clientWidth, behavior: 'smooth' }));
      next.addEventListener('click', () => list.scrollBy({ left: list.clientWidth, behavior: 'smooth' }));
      list.addEventListener('scroll', update, { passive: true });
      this.querySelector('[data-cu-nav]').hidden = false;
      update();
    }
  });
}
